import type { Channel, Program } from "@hybrid-iptv/types";
import { orderCategories } from "../epg/categoryOrder.js";
import { viewerCountry } from "../settings/countryStore.js";
import { normalize, scoreMatch } from "../search/useUniversalSearch.js";

/**
 * The second screen's answers (roadmap Section 25), shaped for the phone page
 * (native/android/app/src/main/assets/remote/app.js). Pure: the bridge
 * (PhoneRemoteBridge.tsx) hands in the channels the TV already shows — parental
 * locks and hidden channels already applied — and the guide's "now playing".
 */

export interface PhoneChannel {
  id: string;
  name: string;
  number: number | null;
  logo: string | null;
  now: { title: string; startMs: number; endMs: number } | null;
}

export type NowPlaying = (channelId: string) => Program | null;

export function phoneChannel(channel: Channel, nowPlaying: NowPlaying): PhoneChannel {
  const now = nowPlaying(channel.id);
  const startMs = now ? Date.parse(now.start) : NaN;
  const endMs = now ? Date.parse(now.end) : NaN;
  return {
    id: channel.id,
    name: channel.name,
    number: channel.number ?? null,
    logo: channel.logoUrl ?? null,
    now: now && Number.isFinite(startMs) && Number.isFinite(endMs) ? { title: now.title, startMs, endMs } : null,
  };
}

/** The Live page's way of naming a category from its id. */
export function categoryName(id: string): string {
  return id.replace(/^cat-/, "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Every category with channels, in the guide's order (the viewer's country and no-country first). */
export function phoneCategories(channels: readonly Channel[]): Array<{ id: string; name: string; count: number }> {
  const counts = new Map<string, number>();
  for (const channel of channels) counts.set(channel.categoryId, (counts.get(channel.categoryId) ?? 0) + 1);
  return orderCategories([...counts].map(([id, count]) => ({ id, name: categoryName(id), count })), viewerCountry());
}

export interface ChannelPageRequest {
  category?: string;
  text?: string;
  offset?: number;
  limit?: number;
}

/** One page of channels: a category and/or a name filter, in channel-number order. */
export function phoneChannelPage(
  channels: readonly Channel[],
  request: ChannelPageRequest,
  nowPlaying: NowPlaying,
): { total: number; items: PhoneChannel[] } {
  const needle = normalize(String(request.text ?? ""));
  const category = typeof request.category === "string" ? request.category : "";
  const matching = channels.filter(
    (channel) => (!category || channel.categoryId === category) && (!needle || normalize(channel.name).includes(needle) || String(channel.number ?? "") === needle),
  );
  const offset = Math.max(0, Math.floor(Number(request.offset) || 0));
  const limit = Math.max(1, Math.min(100, Math.floor(Number(request.limit) || 60)));
  return { total: matching.length, items: matching.slice(offset, offset + limit).map((channel) => phoneChannel(channel, nowPlaying)) };
}

/** Channels for the phone's search: a channel number first, then by how well the name matches. */
export function phoneChannelSearch(channels: readonly Channel[], query: string, nowPlaying: NowPlaying, cap = 15): PhoneChannel[] {
  const needle = normalize(query);
  if (needle.length < 2 && !/^\d+$/.test(needle)) return [];
  return channels
    .map((channel) => ({
      channel,
      score: channel.number !== undefined && String(channel.number) === needle ? 100 : scoreMatch(normalize(channel.name), needle),
    }))
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score || a.channel.name.localeCompare(b.channel.name))
    .slice(0, cap)
    .map((hit) => phoneChannel(hit.channel, nowPlaying));
}
